import moment from 'moment';
import { AdSetWithRelationsModel } from '@mm/state/model/AdSetModel';
import AdAccountModel, {
  CURRENCY_OFFSETS,
} from '@mm/state/model/AdAccountModel';
import {
  DEFAULT_AD_SET_BUDGET,
  DEFAULT_AD_SET_BUDGET_TYPE,
  DEFAULT_AD_SET_NAME,
  DEFAULT_BID_AMOUNT,
  DEFAULT_CALL_TO_ACTION,
  DEFAULT_CAMPAIGN_BUDGET,
  DEFAULT_CAMPAIGN_BUDGET_TYPE,
  DEFAULT_CAMPAIGN_NAME,
  DEFAULT_SPECIAL_AD_CATEGORY,
} from '../../constants/constants';
import { defaultTargeting } from './Targeting/utils';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import { AdModel } from '@mm/state/model/AdModel';

const prepareValuesWithoutAd = (
  adSets: AdSetWithRelationsModel[],
  adAccounts: AdAccountModel[],
) => {
  return {
    name: '',
    campaignName: DEFAULT_CAMPAIGN_NAME,
    adSetName: DEFAULT_AD_SET_NAME,
    callToAction: DEFAULT_CALL_TO_ACTION,
    campaignBudgetType: DEFAULT_CAMPAIGN_BUDGET_TYPE,
    campaignBudget: DEFAULT_CAMPAIGN_BUDGET,
    specialAdCategory: DEFAULT_SPECIAL_AD_CATEGORY,
    optimizationGoal: null,
    adAccount: adAccounts[0] || null,
    sourceAdSet: adSets[0] || null,
    adSetBudgetType: DEFAULT_AD_SET_BUDGET_TYPE,
    adSetBudget: DEFAULT_AD_SET_BUDGET,
    adSetStartDate: moment(),
    adSetEndDate: moment().add(1, 'month'),
    creativeText: '',
    creativeHeadline: '',
    creativeDescription: '',
    bidAmount: DEFAULT_BID_AMOUNT,
    sourceAd: null,
    imageUrl: null,
    videoUrl: null,
    thumbUrl: null,
    thumbAssetId: null,
    videoAssetId: null,
    imageAssetId: null,
    targeting: defaultTargeting(),
  };
};

const campaignBudget = (ad: AdConfigModel, offset: number) =>
  (ad.campaignBudget && ad.campaignBudget / offset) ||
  Math.max(
    DEFAULT_CAMPAIGN_BUDGET,
    (ad.adSetBudget && ad.adSetBudget / offset) || 0,
  );

export const processUrl = (
  url: string | null,
  hasAsset: boolean,
  sourceAdUrl?: string | null,
) => {
  if (url !== null) {
    return url;
  }
  if (hasAsset) {
    return null;
  }
  return sourceAdUrl || null;
};

const campaignNameResolver = (ad: AdConfigModel) =>
  ad.campaignName || DEFAULT_CAMPAIGN_NAME;
const adSetNameResolver = (ad: AdConfigModel) =>
  ad.adSetName || DEFAULT_AD_SET_NAME;
export const offsetForAccount = (adAccount?: AdAccountModel | null): number => {
  return CURRENCY_OFFSETS[(adAccount && adAccount.currency) || 'USD'];
};

export const resolveMismatchingAudiences = (
  adAccount?: AdAccountModel | null,
  adSet?: AdSetWithRelationsModel | null,
  cloneAdSetSelected?: boolean,
) => {
  if (
    !cloneAdSetSelected ||
    !adAccount ||
    !adSet ||
    !adSet.customAudiences ||
    adSet.customAudiences.length === 0
  ) {
    return [];
  }
  const mismatchingAudiences = adSet.customAudiences.filter(setAud => {
    return (
      (adAccount.customAudiences || []).find(
        accountAud => accountAud.id === setAud.id,
      ) === undefined
    );
  });
  return mismatchingAudiences.map(aud => `${aud.name} (${aud.id})`);
};

export const adSetCloning = (adConfig: AdConfigModel | null) =>
  !adConfig || adConfig.targeting === null;
export const adCloning = (adConfig: AdConfigModel | null) =>
  Boolean(adConfig?.sourceAdId);

export const prepareInitialFormValues = (
  ad: AdConfigModel | null,
  adSets: AdSetWithRelationsModel[],
  adAccounts: AdAccountModel[],
  ads: AdModel[],
) => {
  if (!ad) {
    return prepareValuesWithoutAd(adSets, adAccounts);
  }
  const account = adAccounts.find(a => a.id === ad.adAccountId) || null;
  const offset = offsetForAccount(account);
  const adSet = adSets.find(a => a.id === ad.sourceAdSetId) || null;
  const sourceAd = ads.find(a => a.id === ad.sourceAdId) || null;
  return {
    name: '',
    campaignName: campaignNameResolver(ad),
    adSetName: adSetNameResolver(ad),
    callToAction: ad.callToAction || DEFAULT_CALL_TO_ACTION,
    campaignBudgetType: ad.campaignBudgetType || DEFAULT_CAMPAIGN_BUDGET_TYPE,
    campaignBudget: campaignBudget(ad, offset),
    specialAdCategory: ad.specialAdCategory || DEFAULT_SPECIAL_AD_CATEGORY,
    adAccount: account,
    sourceAdSet: adSet,
    optimizationGoal: ad.optimizationGoal,
    adSetBudgetType: ad.adSetBudgetType || DEFAULT_AD_SET_BUDGET_TYPE,
    adSetBudget:
      (ad.adSetBudget && ad.adSetBudget / offset) || DEFAULT_AD_SET_BUDGET,
    adSetStartDate:
      (ad.adSetStartDate && moment(ad.adSetStartDate)) || moment(),
    adSetEndDate:
      (ad.adSetEndDate && moment(ad.adSetEndDate)) || moment().add(1, 'month'),
    creativeText: ad.creativeText || '',
    creativeHeadline: ad.creativeHeadline || '',
    creativeDescription: ad.creativeDescription || '',
    bidAmount: ad.bidAmount && ad.bidAmount / offset,
    sourceAd,
    imageUrl: processUrl(
      ad.imageUrl,
      Boolean(ad.assetId && !ad.videoUrl),
      sourceAd?.imageUrl,
    ),
    videoUrl: processUrl(
      ad.videoUrl,
      Boolean(ad.assetId && !ad.imageUrl),
      sourceAd?.videoUrl,
    ),
    thumbUrl: processUrl(ad.thumbUrl, !!ad.thumbId, sourceAd?.thumbUrl),
    thumbAssetId: ad.thumbId,
    videoAssetId: ad.videoUrl && ad.assetId,
    imageAssetId: ad.imageUrl && ad.assetId,
    targeting: !ad?.targeting ? defaultTargeting() : ad.targeting,
  };
};
